import { Component, Input, output } from '@angular/core';

export type ChubbChartPieData = {
  color: string;
  value: number;
  label: string;
  patternId?: string;
};

type ChubbChartPiePaths = {
  label: string;
  color: string;
  patternId?: string;
  d: string;
  lX: number;
  lY: number;
  percent: number;
};

@Component({
  selector: 'chubb-chart-pie',
  standalone: true,
  imports: [],
  templateUrl: './chubb-chart-pie.component.html'
})
export class ChubbChartPieComponent {
  private static _counter = 0;
  readonly instanceId = ++ChubbChartPieComponent._counter;

  @Input() width: number = 352;
  @Input() height: number = 211;
  @Input() value: ChubbChartPieData[] = [];
  @Input() emptyResponse: boolean = false;
  @Input() showLegend: boolean = false;
  @Input() legendWidth: number = 110;
  readonly chartClick = output<string>();

  get totalWidth(): number {
    return this.showLegend ? this.legendWidth + this.width : this.width;
  }

  get viewBox(): string {
    return `0 0 ${this.totalWidth} ${this.height}`;
  }

  get chartCx(): number {
    return this.showLegend ? this.legendWidth + this.width / 2 : this.width / 2;
  }

  get chartTransform(): string {
    return `translate(${this.chartCx}, ${this.height / 2})`;
  }

  get legendItems(): ChubbChartPieData[] {
    return [...this.value].reverse();
  }

  get legendStartY(): number {
    const itemHeight = 22;
    const totalH = this.value.length * itemHeight;
    return Math.max(0, Math.round((this.height - totalH) / 2));
  }

  private calculatePercents(values: number[]): number[] {
    const total = values.reduce((a, b) => a + b, 0);
    if (total === 0) return values.map(() => 0);

    const rawPercents = values.map(v => (v / total) * 100);
    const result = rawPercents.map((p, i) => values[i] > 0 ? Math.max(1, Math.floor(p)) : 0);
    let remaining = 100 - result.reduce((a, b) => a + b, 0);

    if (remaining > 0) {
      const indices = rawPercents
        .map((p, i) => ({ frac: Math.floor(p) >= 1 ? p - Math.floor(p) : -1, i }))
        .filter(x => x.frac >= 0)
        .sort((a, b) => b.frac - a.frac);

      for (const { i } of indices) {
        if (remaining <= 0) break;
        result[i]++;
        remaining--;
      }
    }

    return result;
  }

  get paths(): ChubbChartPiePaths[] {
    if (!this.value?.length) return [];

    const radius = this.height * 0.4;

    const values = this.value.map(s => Number(s.value || 0));
    const totalValue = values.reduce((sum, v) => sum + v, 0);

    if (totalValue === 0) return [];

    const percents = this.calculatePercents(values);

    const MIN_SLICE_ANGLE = Math.PI / 6;

    const rawAngles = values.map(v =>
      (v / totalValue) * 2 * Math.PI
    );

    let adjustedAngles: number[] = [];
    let excess = 0;
    let adjustableTotal = 0;

    rawAngles.forEach(angle => {
      if (angle > 0 && angle < MIN_SLICE_ANGLE) {
        adjustedAngles.push(MIN_SLICE_ANGLE);
        excess += MIN_SLICE_ANGLE - angle;
      } else {
        adjustedAngles.push(angle);
        adjustableTotal += angle;
      }
    });

    adjustedAngles = adjustedAngles.map((angle, i) => {
      if (rawAngles[i] >= MIN_SLICE_ANGLE && adjustableTotal > 0) {
        return angle - (angle / adjustableTotal) * excess;
      }
      return angle;
    });

    let startAngle = -(Math.PI / 2);

    return this.value.map((sector, index) => {
      const sliceAngle = adjustedAngles[index];

      if (sliceAngle <= 0) return null as any;

      const x1 = radius * Math.cos(startAngle);
      const y1 = radius * Math.sin(startAngle);

      const x2 = radius * Math.cos(startAngle + sliceAngle);
      const y2 = radius * Math.sin(startAngle + sliceAngle);

      const lX = (radius * 2) / 3 * Math.cos(startAngle + sliceAngle / 2);
      const lY = (radius * 2) / 3 * Math.sin(startAngle + sliceAngle / 2);

      const largeArcFlag = sliceAngle > Math.PI ? 1 : 0;

      const d = `M 0 0 L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`.trim();

      startAngle += sliceAngle;

      return {
        label: sector.label,
        color: sector.color,
        patternId: sector.patternId,
        d,
        lX,
        lY,
        percent: percents[index]
      };
    }).filter(Boolean);
  }

  getFilterData(label: string) {
    this.chartClick.emit(label);
  }
}
